import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import '../GL/LoanPayment.css';
import { formatDateToDDMMYYYY, getTodayYYYYMMDD } from '../../utils/dateUtils';
import { maskAadhaar, maskPAN } from '../../utils/dataMaskingUtils';

const TBDLoanPayment = () => {
  const { type, id } = useParams();
  const searchType = type || 'name';
  const [loanDetails, setLoanDetails] = useState(null);
  const [paidDates, setPaidDates] = useState([]);
  const [overdueDetails, setOverdueDetails] = useState([]);
  const [showOverdueDetails, setShowOverdueDetails] = useState(false);
  const [overdueInterestRate, setOverdueInterestRate] = useState(0.5); // Default 0.5% per day
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(getTodayYYYYMMDD());
  const [paymentMode, setPaymentMode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const [dailyCollectionAmount, setDailyCollectionAmount] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [totalOverduePayAmount, setTotalOverduePayAmount] = useState('');
  const [overduePaidDates, setOverduePaidDates] = useState([]);
  const [isPayingOverdue, setIsPayingOverdue] = useState(false);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/tbd-loan-reciever`, {
          params: { type: searchType, value: id },
        });
        setLoanDetails(response.data);
        
        // Calculate daily collection amount
        if (response.data) {
          const dailyAmount = (response.data.loan_amount / response.data.period).toFixed(2);
          setDailyCollectionAmount(parseFloat(dailyAmount));
          setPaymentAmount(dailyAmount);
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
      }
    };

    fetchLoanDetails();
  }, [id, searchType]);

  useEffect(() => {
    if (loanDetails && loanDetails.account_no) {
      const fetchPaidDates = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
          setPaidDates(response.data);
        } catch (error) {
          console.error('Error fetching paid dates:', error);
        }
      };

      const fetchTransactions = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/tbd-daily-transactions/${loanDetails.account_no}`);
          setTransactions(response.data || []);
        } catch (error) {
          console.error('Error fetching transactions:', error);
        }
      };

      const fetchOverduePaidDates = async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/tbd-overdue-paid-dates/${loanDetails.account_no}`);
          const dates = (res.data || []).map(d => typeof d === 'string' ? d.slice(0, 10) : d);
          setOverduePaidDates(dates);
        } catch (err) {
          console.error('Error fetching overdue paid dates:', err);
        }
      };

      fetchPaidDates();
      fetchTransactions();
      fetchOverduePaidDates();
    }
  }, [loanDetails]);

  // Calculate overdue details for daily collection
  useEffect(() => {
    if (loanDetails && dailyCollectionAmount > 0) {
      const calculateOverdueDetails = () => {
        const currentDate = new Date();
        const loanStartDate = new Date(loanDetails.date);
        const period = loanDetails.period;
        
        // Start collection from the next day after loan application
        const firstCollectionDate = new Date(loanStartDate);
        firstCollectionDate.setDate(firstCollectionDate.getDate() + 1);
        
        // Generate all dates from first collection date to current date (or loan end)
        const allDates = [];
        const endDate = new Date(firstCollectionDate);
        endDate.setDate(endDate.getDate() + period - 1);
        
        for (let d = new Date(firstCollectionDate); d <= Math.min(currentDate, endDate); d.setDate(d.getDate() + 1)) {
          allDates.push(new Date(d));
        }
        
        const overdueDetails = allDates
          .filter(date => !paidDates.includes(date.toISOString().split('T')[0])) // Filter out paid dates
          .map(date => {
            const daysOverdue = Math.max(0, Math.floor((currentDate - date) / (1000 * 60 * 60 * 24)));
            const overdueInterest = (overdueInterestRate / 100) * daysOverdue * dailyCollectionAmount;
            
            return {
              dueDate: date.toISOString().split('T')[0],
              dailyAmount: dailyCollectionAmount,
              daysOverdue: daysOverdue,
              overdueInterest: overdueInterest.toFixed(2),
            };
          });
        
        setOverdueDetails(overdueDetails);
        const total = overdueDetails.length > 0
          ? overdueDetails.reduce((sum, d) => sum + parseFloat(d.overdueInterest || 0), 0).toFixed(2)
          : '';
        setTotalOverduePayAmount(total);
      };
      
      calculateOverdueDetails();
    }
  }, [loanDetails, paidDates, overdueInterestRate, dailyCollectionAmount]);
  

  // Handle overdue payment submission (single row)
  const handleOverduePayment = async (detail) => {
    try {
      await axios.post(`${API_BASE_URL}/tbd-overdue-payment`, {
        account_no: loanDetails.account_no,
        debtor_name: loanDetails.loanee_name,
        overdue_paid: detail.overdueInterest,
        payment_date: detail.dueDate,
        daily_amount: detail.dailyAmount,
        days_overdue: detail.daysOverdue,
      });
      alert('Overdue Payment Successful!');
      const response = await axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`);
      setPaidDates(response.data);
      const overdueRes = await axios.get(`${API_BASE_URL}/tbd-overdue-paid-dates/${loanDetails.account_no}`);
      setOverduePaidDates(overdueRes.data || []);
    } catch (error) {
      console.error('Error processing overdue payment:', error);
      alert('Overdue Payment failed. Please try again.');
    }
  };

  // Pay total overdue amount (distributed across pending overdue dates, oldest first)
  const handlePayTotalOverdue = async () => {
    const amount = parseFloat(totalOverduePayAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Please enter a valid amount to pay.');
      return;
    }
    const pending = overdueDetails.filter(d => !overduePaidDates.includes(d.dueDate));
    if (pending.length === 0) {
      alert('No pending overdue dates to pay.');
      return;
    }
    setIsPayingOverdue(true);
    try {
      await axios.post(`${API_BASE_URL}/tbd-overdue-payment-distribute`, {
        account_no: loanDetails.account_no,
        loanee_name: loanDetails.loanee_name,
        amount_to_pay: amount,
        payment_mode: paymentMode || 'Overdue Payment',
        transaction_id: transactionId.trim() || undefined,
        overdue_details: pending.map(d => ({
          dueDate: d.dueDate,
          dailyAmount: d.dailyAmount,
          daysOverdue: d.daysOverdue,
          overdueInterest: parseFloat(d.overdueInterest) || 0,
        })),
      });
      alert('Overdue payment distributed successfully.');
      const res = await axios.get(`${API_BASE_URL}/tbd-overdue-paid-dates/${loanDetails.account_no}`);
      setOverduePaidDates((res.data || []).map(d => typeof d === 'string' ? d.slice(0, 10) : d));
      setTotalOverduePayAmount(
        overdueDetails
          .filter(d => !(res.data || []).includes(d.dueDate))
          .reduce((s, d) => s + parseFloat(d.overdueInterest || 0), 0)
          .toFixed(2)
      );
    } catch (error) {
      console.error('Error paying total overdue:', error);
      alert(error.response?.data?.error || 'Overdue payment failed. Please try again.');
    } finally {
      setIsPayingOverdue(false);
    }
  };

  // Handle daily payment submission
  const handlePayment = async () => {
    // Prevent double-clicks
    if (isProcessingPayment) {
      return;
    }

    if (!selectedPaymentDate || !paymentMode) {
      alert('Please fill in payment date and mode.');
      return;
    }
    const amount = Number(paymentAmount);
    if (!amount || amount <= 0) {
      alert('Enter a valid payment amount.');
      return;
    }

    setIsProcessingPayment(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/tbd-daily-payment`, {
        account_no: loanDetails.account_no,
        payment_date: selectedPaymentDate,
        payment_mode: paymentMode,
        transaction_id: transactionId.trim() || null,
        daily_amount: amount,
        loanee_name: loanDetails.loanee_name,
      });
      // Show success message with excess payment info if applicable
      const successMessage = response.data?.message || 'Daily Payment Successful!';
      alert(successMessage);
      setIsPaymentSuccessful(true);
      setSelectedPaymentDate(getTodayYYYYMMDD());
      setPaymentMode('');
      setTransactionId('');
      setPaymentAmount(dailyCollectionAmount.toFixed(2));
      
      // Refresh paid dates and transactions
      const [paidDatesRes, transactionsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/tbd-fetch-paid-dates/${loanDetails.account_no}`),
        axios.get(`${API_BASE_URL}/tbd-daily-transactions/${loanDetails.account_no}`)
      ]);
      setPaidDates(paidDatesRes.data);
      setTransactions(transactionsRes.data || []);
    } catch (error) {
      if (error.response && error.response.data.error === 'Already Paid') {
        alert('Payment for this date is already made');
      } else {
        console.error('Error processing payment:', error);
        alert('Payment failed. Please try again.');
      }
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Generate and print payment slip
  const generateSlip = (opts = {}) => {
    const { payment } = opts; // payment: { amount, payment_date, payment_mode, transaction_id }
    const amountToShow = payment?.amount ?? (Number(paymentAmount) || dailyCollectionAmount);
    const dateToShow = payment?.payment_date ? new Date(payment.payment_date) : new Date(selectedPaymentDate);
    const modeToShow = payment?.payment_mode || paymentMode || '';
    const txToShow = payment?.transaction_id || transactionId || '';

    const slipContent = `
  
<style>
  body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
  .container { display: flex; }
  .left-receipt { width: 40%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .right-receipt { width: 60%; border: 1px solid #000; padding: 15px; box-sizing: border-box; }
  .header { text-align: center; font-weight: bold; font-size: 1.2em; margin-bottom: 5px; }
  .sub-header { text-align: center; font-size: 0.9em; margin-bottom: 15px; }
  .details { margin: 10px 0; }
  .line { display: flex; justify-content: space-between; margin: 5px 0; }
  .table { width: 100%; border-collapse: collapse; margin-top: 10px; }
  .table td { padding: 5px; }
  .table .right { text-align: right; font-weight: bold; }
  .total { border-top: 1px solid #000; margin-top: 10px; padding-top: 5px; font-weight: bold; }
  .footer { text-align: center; margin-top: 15px; font-size: 0.9em; }
</style>

<div class="container">
  <!-- Left Receipt -->
  <div class="left-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
    <div class="details">
    <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
    <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
    <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(dateToShow)}</div>
   </div>
    <table class="table">
        <tr>
          <td>DAILY COLLECTION PAID</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
    <div class="footer">
      Cashier / Mg. Partner                      
    </div>
  </div>

  <!-- Right Receipt -->
  <div class="right-receipt">
    <div class="header">SHREE SIDDHARAMESHWAR FINANCE AND LEASING</div>
    <div class="sub-header">Kasgi</div>
    <div class="details">
      <div class="line"><strong>A/C No:</strong> ${loanDetails.account_no}</div>
      <div class="line"><strong>Date:</strong> ${formatDateToDDMMYYYY(new Date())}</div>
    </div>
   <div class="details">
     
      <div class="line"><strong>Received from Sri:</strong> ${loanDetails.loanee_name}</div>
      <div class="line"><strong>Daily Collection Amount Rs:</strong> ₹${Number(amountToShow).toFixed(2)}</div>
      <div class="line"><strong>Payment Date:</strong> ${formatDateToDDMMYYYY(dateToShow)}</div>
    </div>
    <table class="table">
      <tr>
          <td>TBD Interest A/c</td>
          <td class="right">${Number(amountToShow).toFixed(2)}</td>
        </tr>
      </table>
      <div class="total">
        TOTAL: ₹${Number(amountToShow).toFixed(2)}
      </div>
    </table>
    <div class="footer">
      Cashier / Mg. Partner
    </div>
  </div>
</div>
`;
  const printWindow = window.open('', '', 'height=800,width=1200');
  printWindow.document.write('<html><head><title>Interest Slip</title></head><body>');
  printWindow.document.write(slipContent);
  printWindow.document.write('</body></html>');
  printWindow.document.close();
  printWindow.print();
};

  // Toggle overdue details table on Ctrl + B key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'b') {
        setShowOverdueDetails((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!loanDetails) {
    return <h2>Loading loan details...</h2>;
  }
  return (
    <div className="loan-payment-container">
      <div className="loan-payment-header">TBD Daily Collection Payment</div>
      {/* Loan Details */}
      <div className="loan-details">
        <p><strong>Account No:</strong> {loanDetails.account_no}</p>
        <p><strong>Loanee Name:</strong> {loanDetails.loanee_name}</p>
        <p><strong>Date:</strong> {formatDateToDDMMYYYY(loanDetails.date)}</p>
        <p><strong>Aadhaar Debtor:</strong> {maskAadhaar(loanDetails.aadhaar_debtor)}</p>
        <p><strong>PAN Card:</strong> {maskPAN(loanDetails.pan_card)}</p>
        <p><strong>Address Debtor:</strong> {loanDetails.address_debtor}</p>
        <p><strong>Guarantor Name:</strong> {loanDetails.guarantor_name}</p>
        <p><strong>Loan Amount:</strong> ₹{loanDetails.loan_amount}</p>
        <p><strong>Period:</strong> {loanDetails.period} days</p>
        <p><strong>Daily Collection Amount:</strong> ₹{dailyCollectionAmount.toFixed(2)}</p>
        <p><strong>Collector Name:</strong> {loanDetails.collector_name || 'Not specified'}</p>
      </div>
      {/* Payment History */}
      <div className="payment-history">
        <h3>Payment History</h3>
        {(() => {
          const totalAmountPaid = transactions.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);
          const paidDays = Math.floor(totalAmountPaid / dailyCollectionAmount);
          const remainingDays = Math.max(0, loanDetails.period - paidDays);
          
          return (
            <>
              <p><strong>Total Paid Days:</strong> {paidDays} out of {loanDetails.period} days</p>
              <p><strong>Remaining Days:</strong> {remainingDays} days</p>
              <p><strong>Total Amount Paid:</strong> ₹{totalAmountPaid.toFixed(2)}</p>
              <p><strong>Remaining Amount:</strong> ₹{Math.max(0, (loanDetails.loan_amount - totalAmountPaid)).toFixed(2)}</p>
            </>
          );
        })()}
      </div>
      {/* Overdue Details */}
      {showOverdueDetails && (
        <div className="overdue-details">
          <h3>Overdue Details (Press Ctrl+B to toggle)</h3>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '25px', flexWrap: 'wrap' }}>
            <div className="overdue-rate-section" style={{ flex: '1', minWidth: '250px' }}>
              <label>Overdue Interest Rate (% per day):</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={overdueInterestRate}
                onChange={(e) => setOverdueInterestRate(e.target.value)}
              />
            </div>
            <div className="overdue-rate-section" style={{ flex: '1', minWidth: '250px' }}>
              <label>Total Overdue Interest:</label>
              <div className="overdue-total-actions">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={totalOverduePayAmount}
                  onChange={(e) => setTotalOverduePayAmount(e.target.value)}
                  placeholder={overdueDetails.reduce((sum, d) => sum + parseFloat(d.overdueInterest || 0), 0).toFixed(2)}
                  className="overdue-total-amount-input"
                  style={{
                    padding: '12px 15px',
                    fontSize: '16px',
                    borderRadius: '6px',
                    border: '2px solid #d3b899',
                    backgroundColor: '#faf5f0',
                    color: '#4d3221',
                    fontWeight: 'bold'
                  }}
                />
                <span style={{ color: '#4d3221', fontWeight: 'bold' }}>₹</span>
                <button
                  type="button"
                  className="pay-overdue-btn"
                  onClick={handlePayTotalOverdue}
                  disabled={isPayingOverdue || !totalOverduePayAmount || parseFloat(totalOverduePayAmount) <= 0 || overdueDetails.filter(d => !overduePaidDates.includes(d.dueDate)).length === 0}
                >
                  {isPayingOverdue ? 'Processing...' : 'Pay Total Overdue'}
                </button>
              </div>
              <div style={{ fontSize: '12px', color: '#6a4631', marginTop: '4px' }}>
                Default: total (₹{overdueDetails.reduce((sum, d) => sum + parseFloat(d.overdueInterest || 0), 0).toFixed(2)}). Edit to pay a different amount; it will be distributed to pending overdue dates (oldest first).
              </div>
            </div>
          </div>
          <table className="overdue-table">
            <thead>
              <tr>
                <th>Due Date</th>
                <th>Daily Amount</th>
                <th>Days Overdue</th>
                <th>Overdue Interest</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {overdueDetails.length > 0 ? (
                overdueDetails.map((detail, index) => {
                  const isPaid = overduePaidDates.includes(detail.dueDate);
                  return (
                    <tr key={index} style={isPaid ? { backgroundColor: 'rgba(40, 167, 69, 0.1)' } : {}}>
                      <td>{formatDateToDDMMYYYY(detail.dueDate)}</td>
                      <td>₹{parseFloat(detail.dailyAmount).toFixed(2)}</td>
                      <td>{detail.daysOverdue}</td>
                      <td>₹{detail.overdueInterest}</td>
                      <td>
                        {isPaid ? (
                          <span style={{ color: '#28a745', fontWeight: 'bold' }}>Paid</span>
                        ) : (
                          <button 
                            className="pay-overdue-btn"
                            onClick={() => handleOverduePayment(detail)}
                          >
                            Pay Overdue
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#6a4631' }}>
                    No overdue payments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Payment Form */}
      <div className="payment-form">
        <h3>Make Daily Payment</h3>
        <div>
          <label>Payment Date:</label>
          <input
            type="date"
            value={selectedPaymentDate}
            onChange={(e) => setSelectedPaymentDate(e.target.value)}
            max={getTodayYYYYMMDD()}
          />
        </div>
        <div>
          <label>Daily Collection Amount:</label>
          <input
            type="number"
            step="0.01"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            placeholder="Enter amount"
          />
          {selectedPaymentDate && (() => {
            const expectedAmount = dailyCollectionAmount;
            const amount = Number(paymentAmount) || 0;
            const excess = amount > expectedAmount ? amount - expectedAmount : 0;
            return (
              <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                <div>Expected daily amount: ₹{expectedAmount.toFixed(2)}</div>
                {excess > 0 && (
                  <div style={{ color: '#28a745', fontWeight: 'bold', marginTop: '3px' }}>
                    Excess ₹{excess.toFixed(2)} will be applied to previous remaining days and future days
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <div>
  <label>Payment Mode:</label>
  <select
    value={paymentMode}
    onChange={(e) => setPaymentMode(e.target.value)}
  >
    <option value="">Select</option>
    <option value="Cash">Cash</option>
    <option value="UPI">UPI</option>
    <option value="Cheque">Cheque</option>
  </select>
</div>
<div>
          <label>Transaction ID (Optional):</label>
          <input
            type="text"
            value={transactionId}
            onChange={(e) => setTransactionId(e.target.value)}
            placeholder="Enter Transaction ID (Optional)"
          />
        </div>
        <button onClick={handlePayment} disabled={isProcessingPayment}>
          {isProcessingPayment ? 'Processing...' : 'Submit Daily Payment'}
        </button>
      </div>

      {/* Slip Button */}
      {isPaymentSuccessful && (
        <button onClick={() => generateSlip()} className="slip-button">Generate Slip</button>
      )}

      {/* View All Slips Modal Trigger */}
      <div style={{ marginTop: 10 }}>
        <button onClick={() => setShowSlipModal(true)}>View All Payment Slips</button>
      </div>

      {showSlipModal && (
        <div className="modal-overlay" onClick={() => setShowSlipModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payment Slips</h3>
              <button className="modal-close" onClick={() => setShowSlipModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {(transactions || []).map((tx, idx) => (
                <div key={idx} className="slip-row">
                  <div><strong>Date:</strong> {tx.payment_date ? formatDateToDDMMYYYY(tx.payment_date) : '-'}</div>
                  <div><strong>Mode:</strong> {tx.payment_mode}</div>
                  <div><strong>Txn:</strong> {tx.transaction_id || '-'}</div>
                  <div><strong>Amount:</strong> ₹{Number(tx.amount).toFixed(2)}</div>
                  <div>
                    <button onClick={() => generateSlip({ payment: tx })}>Print</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TBDLoanPayment;
